"use client"

import { useRef, useState, useEffect } from "react"
import PropTypes from "prop-types"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  Typography,
  Link,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import DownloadIcon from "@mui/icons-material/Download"
import PrintIcon from "@mui/icons-material/Print"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"

const isIosSafari = () => {
  if (typeof navigator === "undefined") return false
  const ua = navigator.userAgent
  return /iPad|iPhone|iPod/.test(ua) && /WebKit/.test(ua) && !/Chrome/.test(ua)
}

/**
 * Modal reutilizable que muestra un PDF en un iframe embebido.
 * El Blob URL se crea y revoca internamente: cero leaks.
 */
// No usar theme.breakpoints aquí: este modal vive en PdfPreviewProvider, por encima del
// ThemeProvider de App, y useTheme() puede no traer el objeto de breakpoints con .down().
// String query equivale a breakpoints.down('sm') del default MUI (sm = 600px).
const FULL_SCREEN_MAX_WIDTH = "(max-width:599px)"

export default function PdfPreviewModal({ open, blob, fileName, title, onClose }) {
  const fullScreen = useMediaQuery(FULL_SCREEN_MAX_WIDTH)

  const iframeRef = useRef(null)
  const [blobUrl, setBlobUrl] = useState(null)
  const [iframeLoading, setIframeLoading] = useState(true)
  const [printError, setPrintError] = useState(false)

  const ios = isIosSafari()

  // Crear / revocar URL de blob cuando cambia el blob
  useEffect(() => {
    if (!blob) return

    const url = URL.createObjectURL(blob)
    setBlobUrl(url)
    setIframeLoading(true)
    setPrintError(false)

    return () => {
      URL.revokeObjectURL(url)
      setBlobUrl(null)
    }
  }, [blob])

  // Revocar al cerrar también
  const handleClose = () => {
    onClose()
  }

  const handleDownload = () => {
    if (!blobUrl || !fileName) return
    const a = document.createElement("a")
    a.href = blobUrl
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const handlePrint = () => {
    if (!iframeRef.current) return
    try {
      iframeRef.current.contentWindow.focus()
      iframeRef.current.contentWindow.print()
    } catch (err) {
      setPrintError(true)
    }
  }

  const handleOpenTab = () => {
    if (!blobUrl) return
    const tab = window.open(blobUrl, "_blank")
    if (tab) tab.opener = null
  }

  const displayTitle = title || fileName || "Vista previa"

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { height: fullScreen ? "100%" : "90vh", display: "flex", flexDirection: "column" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          py: 1.5,
          pr: 1.5,
        }}
      >
        <Typography variant="subtitle1" noWrap sx={{ flexGrow: 1, fontWeight: 600 }}>
          {displayTitle}
        </Typography>
        <Tooltip title="Cerrar">
          <IconButton onClick={handleClose} size="small" aria-label="Cerrar visor de PDF">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ flex: 1, p: 0, position: "relative", overflow: "hidden", display: "flex", flexDirection: "column" }}
      >
        {/* Spinner mientras el iframe carga */}
        {iframeLoading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.75)",
              zIndex: 2,
            }}
            aria-label="Cargando PDF"
          >
            <CircularProgress size={40} />
          </Box>
        )}

        {/* iOS Safari no renderiza PDFs en iframe: mostrar mensaje con link */}
        {ios ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Tu navegador no puede previsualizar PDFs embebidos.
            </Typography>
            {blobUrl && (
              <Link href={blobUrl} target="_blank" rel="noopener noreferrer" underline="always">
                Abrir PDF en nueva pestaña
              </Link>
            )}
          </Box>
        ) : (
          blobUrl && (
            <iframe
              ref={iframeRef}
              src={blobUrl}
              title={displayTitle}
              style={{ flex: 1, width: "100%", border: "none", display: "block" }}
              onLoad={() => setIframeLoading(false)}
              aria-label={`Vista previa de ${displayTitle}`}
            />
          )
        )}
      </DialogContent>

      <DialogActions sx={{ gap: 1, px: 2, py: 1.5, flexWrap: "wrap" }}>
        {printError && (
          <Typography variant="caption" color="warning.main" sx={{ flex: 1 }}>
            Imprimir falló en este navegador. Usa Abrir en pestaña para imprimir.
          </Typography>
        )}

        {!ios && (
          <Tooltip title="Imprimir">
            <Button
              size="small"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={!blobUrl || iframeLoading}
              aria-label="Imprimir PDF"
            >
              Imprimir
            </Button>
          </Tooltip>
        )}

        <Tooltip title="Abrir en pestaña nueva">
          <Button
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenTab}
            disabled={!blobUrl}
            aria-label="Abrir PDF en pestaña nueva"
          >
            Abrir en pestaña
          </Button>
        </Tooltip>

        <Button
          size="small"
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={!blobUrl}
          aria-label={`Descargar ${fileName}`}
        >
          Descargar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

PdfPreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  blob: PropTypes.instanceOf(Blob),
  fileName: PropTypes.string,
  title: PropTypes.string,
  onClose: PropTypes.func.isRequired,
}
